import React, { useState, useEffect, useCallback } from 'react';
import { talukaAPI } from '../../services/api';

function BarChart({ data, labelKey, valueKey, color = '#1565c0' }) {
  const max = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} className="tk-bar-row">
          <span className="tk-bar-label" title={d[labelKey]}>
            {String(d[labelKey] || '').length > 16 ? String(d[labelKey]).slice(0, 15) + '…' : d[labelKey]}
          </span>
          <div className="tk-bar-track">
            <div className="tk-bar-fill" style={{ width: `${(Number(d[valueKey]) / max) * 100}%`, background: color }} />
          </div>
          <span className="tk-bar-value">{Number(d[valueKey]).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function aggregateBy(arr, keyField, valueField = 'count') {
  const map = {};
  arr.forEach(r => {
    const key = r[keyField] || 'Unknown';
    map[key] = (map[key] || 0) + Number(r[valueField] || 0);
  });
  return Object.entries(map).map(([k, v]) => ({ [keyField]: k, [valueField]: v }));
}

function buildGPGenderTable(gps, genderByGP) {
  const gpMap = {};
  gps.forEach(g => {
    gpMap[g.id] = { gp_name: g.name, total: 0, male: 0, female: 0, other: 0, households: Number(g.total_households || 0) };
  });
  genderByGP.forEach(r => {
    if (!gpMap[r.tenant_id]) return;
    const cnt = Number(r.count || 0);
    gpMap[r.tenant_id].total += cnt;
    const g = (r.gender || '').toLowerCase();
    if (g === 'male') gpMap[r.tenant_id].male += cnt;
    else if (g === 'female') gpMap[r.tenant_id].female += cnt;
    else gpMap[r.tenant_id].other += cnt;
  });
  return Object.values(gpMap);
}

export default function TalukaPopulation() {
  const [raw, setRaw]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await talukaAPI.getPopulation();
      setRaw(res.data?.data || res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load population data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading…</div>;
  if (error)   return <div style={{ textAlign: 'center', padding: 60, color: '#c62828' }}>{error}</div>;

  const gps           = raw?.grampanchayats  || [];
  const genderByGP    = raw?.genderByGP      || [];
  const ageByGP       = raw?.ageByGP         || [];
  const houseByCategory = raw?.householdsByCategory || [];

  // Derived aggregates
  const byGP      = gps.map(g => ({ gp_name: g.name, population: Number(g.population || 0) }));
  const households = gps.map(g => ({ gp_name: g.name, count: Number(g.total_households || 0) }));
  const byGender  = aggregateBy(genderByGP, 'gender');
  const byAge     = aggregateBy(ageByGP, 'age_group');
  const byCategory = aggregateBy(houseByCategory, 'category');
  const byGPGender = buildGPGenderTable(gps, genderByGP);

  const totalPopulation  = byGP.reduce((s, r) => s + r.population, 0);
  const totalHouseholds  = households.reduce((s, r) => s + r.count, 0);
  const maleCount   = genderByGP.filter(r => r.gender === 'male').reduce((s, r) => s + Number(r.count), 0);
  const femaleCount = genderByGP.filter(r => r.gender === 'female').reduce((s, r) => s + Number(r.count), 0);

  return (
    <div>
      <div className="tk-page-header">
        <h1>Population &amp; Statistics</h1>
        <p>Demographic breakdown across all Grampanchayats</p>
      </div>

      <div className="tk-stat-grid">
        <div className="tk-stat-card">
          <div className="tk-stat-num">{totalPopulation.toLocaleString()}</div>
          <div className="tk-stat-label">Total Population</div>
        </div>
        <div className="tk-stat-card green">
          <div className="tk-stat-num">{maleCount.toLocaleString()}</div>
          <div className="tk-stat-label">Male</div>
        </div>
        <div className="tk-stat-card purple">
          <div className="tk-stat-num">{femaleCount.toLocaleString()}</div>
          <div className="tk-stat-label">Female</div>
        </div>
        <div className="tk-stat-card orange">
          <div className="tk-stat-num">{totalHouseholds.toLocaleString()}</div>
          <div className="tk-stat-label">Households</div>
        </div>
        <div className="tk-stat-card teal">
          <div className="tk-stat-num">
            {totalHouseholds && totalPopulation ? (totalPopulation / totalHouseholds).toFixed(1) : '—'}
          </div>
          <div className="tk-stat-label">Avg per Household</div>
        </div>
      </div>

      <div className="tk-charts-grid">
        {byGender.length > 0 && (
          <div className="tk-chart-card">
            <h4>Gender Distribution</h4>
            <BarChart data={byGender} labelKey="gender" valueKey="count" color="#1565c0" />
          </div>
        )}
        {byAge.length > 0 && (
          <div className="tk-chart-card">
            <h4>Age Groups</h4>
            <BarChart data={byAge} labelKey="age_group" valueKey="count" color="#e65100" />
          </div>
        )}
        {byCategory.length > 0 && (
          <div className="tk-chart-card">
            <h4>Household Category</h4>
            <BarChart data={byCategory} labelKey="category" valueKey="count" color="#6a1b9a" />
          </div>
        )}
        {byGP.some(g => g.population > 0) && (
          <div className="tk-chart-card">
            <h4>Population per GP</h4>
            <BarChart data={byGP} labelKey="gp_name" valueKey="population" color="#2e7d32" />
          </div>
        )}
        {households.some(h => h.count > 0) && (
          <div className="tk-chart-card">
            <h4>Households per GP</h4>
            <BarChart data={households} labelKey="gp_name" valueKey="count" color="#00695c" />
          </div>
        )}
      </div>

      {byGPGender.length > 0 && (
        <div className="tk-card">
          <h3>Gender Breakdown by Grampanchayat</h3>
          <div className="tk-table-wrap">
            <table className="tk-table">
              <thead>
                <tr><th>Grampanchayat</th><th>Total</th><th>Male</th><th>Female</th><th>Other</th><th>Households</th></tr>
              </thead>
              <tbody>
                {byGPGender.map((r, i) => (
                  <tr key={i}>
                    <td><strong>{r.gp_name}</strong></td>
                    <td>{r.total.toLocaleString()}</td>
                    <td>{r.male.toLocaleString()}</td>
                    <td>{r.female.toLocaleString()}</td>
                    <td>{r.other.toLocaleString()}</td>
                    <td>{r.households.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
